import './index.scss'
import ResponsivePageTitle from '../../components/core/title/ResponsivePageTitle'
import {PageSubTitle, PageTitle} from '../../components/core/title'
import Footer from '../../components/layouts/footer'
import ContentLoader from '../../components/core/loader'
import {
    deleteSubscription,
    getSubscriptions,
    reactivateSubscription
} from '../../stores/payment'
import {useDispatch, useSelector} from 'react-redux'
import {useEffect, useState} from 'react'
import Confirm from '../../components/core/confirm'
import classNames from 'classnames'
import {useNamespaceTranslations} from '../../utils/hooks/useNamespacesTranslations'
import SubscriptionList from '../../components/pages/billings/SubscriptionList'
import EmptyContent from '../resources/EmptyContent'
import TrialPeriodContent from '../../components/pages/billings/TrialPeriodContent'
import {Link, useNavigate} from 'react-router-dom'
import NavigationHelper from '../../utils/navigation'
import {routes} from '../../routes'
import localStorageManager from '../../utils/localStorageManager'
import localStorageKeys from '../../utils/localStorageKeys'
import LimitManager from '../../utils/limitManager'
import {setConfirmIsActive} from '../../stores/alert'
import {WarningIcon} from '../../icons'
import {SmallButton} from '../../components/core/button'
import TagManager from 'react-gtm-module'
import tagManager from '../../data/tagManager.json'

const Subscriptions = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const text = useNamespaceTranslations('billings')
    const subscriptions = useSelector(state => state.payment.subscriptions)
    const paymentData = useSelector(state => state.payment.paymentData)
    const isPaymentFetching = useSelector(state => state.payment.isPaymentFetching)
    const isFetching = useSelector(state => state.payment.isFetching)
    const isDeleting = useSelector(state => state.payment.isDeleting)
    const isDarkMode = useSelector(state => state.sidebar.isDarkMode)
    const isTrialExpired = new Date(localStorageManager.get(localStorageKeys.TRIAL)?.expires) < new Date()
    const isTrialPeriod = LimitManager.isTrialPeriod() && !isTrialExpired

    const [deletingSubscriptionId, setDeletingSubscriptionId] = useState('')
    const [processingId, setProcessingId] = useState('')
    const [filteredSubscriptions, setFilteredSubscriptions] = useState(subscriptions?.data?.subscriptions)
    const [showUpgradePlan, setShowUpgradePlan] = useState(false)

    const getEmptyContent = () => {
        if (isTrialPeriod) {
            return <TrialPeriodContent isDarkMode={isDarkMode}/>
        }

        return (
            <EmptyContent
                message={text?.no_subscription}
                hideButton={false}
                buttonText={text.plans}
                showIcon={false}
                onclick={() => navigate(NavigationHelper.getLocalizedPath(routes.plans.paths))}
            />
        )
    }

    const cancelSubscription = async() => {
        dispatch(setConfirmIsActive(false))
        const success = await dispatch(deleteSubscription(deletingSubscriptionId))
        if (!success) return
        setDeletingSubscriptionId('')
        setFilteredSubscriptions(filteredSubscriptions?.map(subscription =>
            subscription?.subscription_id === deletingSubscriptionId
                ? ({...subscription, can_be_canceled: false, can_be_reactivated: true})
                : subscription
        ))

        TagManager.dataLayer({
            dataLayer: {
                event: tagManager.events.formSubmission,
                formAction: tagManager.formActions.deny_trial
            }
        })
    }

    const activateSubscription = async(id) => {
        setProcessingId(id)
        const success = await dispatch(reactivateSubscription(id))
        if (!success) return
        setProcessingId('')
        setFilteredSubscriptions(filteredSubscriptions?.map(subscription =>
            subscription?.subscription_id === id
                ? ({...subscription, can_be_canceled: true, can_be_reactivated: false})
                : subscription
        ))
    }

    const prepareCancelSub = (subscriptionId) => {
        dispatch(setConfirmIsActive(true))
        setDeletingSubscriptionId(subscriptionId)
    }

    useEffect(() => {
        dispatch(getSubscriptions(true))
    }, [])

    useEffect(() => {
        subscriptions?.data?.subscriptions?.every(item => item?.active === false) && setShowUpgradePlan(true)
    }, [subscriptions])

    return (
        <>
            <main className={classNames('main subscriptions', {
                dark: isDarkMode
            })}>
                <Confirm
                    body={text.are_you_sure}
                    callback={cancelSubscription}
                    showLoader={isDeleting}
                />
                <div>
                    <ResponsivePageTitle/>
                    <div className="content-title">
                        <div className="left-side">
                            <div>
                                <PageSubTitle>
                                    <p className={classNames('section', {
                                        dark: isDarkMode
                                    })}>{text.section}</p>
                                </PageSubTitle>
                                <PageTitle>
                                    <h1  className={classNames('page-title', {
                                        dark: isDarkMode
                                    })}>{text.title}</h1>
                                </PageTitle>
                            </div>
                        </div>
                    </div>
                    {
                        isFetching ? (
                            <ContentLoader/>
                        ) :
                            <>
                                {!isTrialPeriod && showUpgradePlan &&
                                    <div className={classNames('upgrade-plan', {
                                        dark: isDarkMode
                                    })}>
                                        <span className='upgrade-plan-left'>
                                            <WarningIcon/>
                                            <span>{text.inactive_text}</span>
                                        </span>
                                        <SmallButton className={classNames({
                                            dark: isDarkMode
                                        })}>
                                            <Link to={NavigationHelper.getLocalizedPath(routes.plans.paths)}>
                                                {text.upgrade_plan}
                                            </Link>
                                        </SmallButton>
                                    </div>}

                                {subscriptions?.data?.subscriptions?.length > 0 ?
                                    <SubscriptionList
                                        allSubscriptions={subscriptions.data.subscriptions}
                                        text={text}
                                        paymentData={paymentData}
                                        isPaymentFetching={isPaymentFetching}
                                        setDeletingSubscriptionId={setDeletingSubscriptionId}
                                        processingId={processingId}
                                        onActivate={activateSubscription}
                                        filteredSubscriptions={filteredSubscriptions}
                                        setFilteredSubscriptions={setFilteredSubscriptions}
                                        onPrepareCancelSub={prepareCancelSub}
                                        isDarkMode={isDarkMode}/>
                                    : getEmptyContent()}
                            </>
                    }
                </div>
                <Footer/>
            </main>
        </>
    )
}

export default Subscriptions
